<template>
  <div v-if="items.length" class="custom-fields" :class="['custom-fields--length-' + items.length]">
    <Field
      v-for="(item, index) in items"
      :parent-index="index"
      :key="item.key"
      :value="get(item.key)"
      :schema="item"
      :default-expanded="defaultExpanded"
      v-on="{
        ...$listeners,
        input: $ev => onInput($ev, item),
        remove: $ev => $emit('remove', $ev),
        'update:expanded:lists': $ev => setExpand($ev),
      }"
    />
  </div>
</template>

<script>
import { Options } from './index'
import Field from './Field'
import { Filter, isList, SortItems } from './utils'
import { assocPath, path } from 'ramda'
import StorageMixin from './mixins/Storage'

export default {
  mixins: [
    StorageMixin({
      target: 'expandedList',
      key() {
        return this.storageKey
      },
      storage: 'localStorage',
    }),
  ],
  props: {
    schema: {
      type: Array,
      default: () => [],
    },
    data: Object,
    placement: [String, Array],
    keys: [String, Array],
    validator: Object,
    validatorPrefix: String,
    components: Array,
    disabledFields: Object,
    disabledFieldsPrefix: {
      type: String,
      default() {
        return this.validatorPrefix
      },
    },
  },
  watch: {
    '$attrs.value': {
      handler(val) {
        this.value = val || {}
      },
      immediate: true,
    },
  },
  data() {
    return {
      storageKey: 'custom-fields-expanded-lists',
      expandedList: [],
      promise: null,
      value: {},
    }
  },
  computed: {
    defaultExpanded() {
      const parentPlacement = this.placement instanceof Array ? this.placement : [this.placement]
      const expandedItems = (this.items.filter(i => path(['options', 'expanded'], i)) || []).map(
        i => `${i.placement}:${i.key}`,
      )

      //get items from localstorage
      let base =
        [
          ...JSON.parse(window['localStorage'].getItem(this.storageKey) || '[]'),
          ...expandedItems,
        ] || []

      if (
        base.some(item => {
          //check for items with parent placement defined
          let values = item.split(':')
          values.splice(-1)
          return parentPlacement.includes(values.join(':'))
        })
      ) {
        return base
      }

      let items = this.items
      if (items.length) {
        const defaultItems = items.slice(0, 1).map(i => `${i.placement}:${i.key}`)

        base = [...new Set(base.concat(defaultItems))]
      }

      return base
    },
    items() {
      const items = Filter(this.schema, { placement: this.placement, keys: this.keys })

      return SortItems(items).map(i => ({
        ...i,
        isList: isList(i),
      }))
    },
  },
  provide() {
    return {
      injection: Object.defineProperties(
        {},
        {
          data: { get: () => this.data || this.value },
          validator: { get: () => this.validator },
          validatorPrefix: { get: () => this.validatorPrefix },
          components: { get: () => [...(Options.components || []), ...(this.components || [])] },
          parentSlots: { get: () => this.$slots },
          disabledFields: { get: () => this.disabledFields },
          disabledFieldsPrefix: { get: () => this.disabledFieldsPrefix },
        },
      ),
    }
  },
  methods: {
    onInput(val, item) {
      this.value = assocPath(item.key.split('.'), val, this.value)
      this.$emit('input', this.value)
    },
    get(key) {
      return path(key.split('.'), this.value)
    },
    setExpand(item) {
      //add / remove expanded lists to localstorage
      const { method, key, placement } = item
      let fullkey = `${placement}:${key}`

      switch (method) {
        case 'remove':
          const index = this.expandedList.indexOf(fullkey)
          this.expandedList.splice(index, 1)
          break
        case 'add':
          this.expandedList.push(fullkey)
          break
      }

      return (this.expandedList = [...new Set(this.expandedList)])
    },
  },

  components: {
    Field,
  },
}
</script>

<style lang="scss">
.custom-fields {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 1rem 2rem;

  .kvass-media-droparea,
  .elder-file {
    min-height: 300px;
  }
}
</style>
